"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { m as motion } from "framer-motion";
import {
  ArrowRight,
  Twitter,
  Github,
  Linkedin,
  Mail,
  TrendingUp,
  MailOpen,
  User,
  Zap,
  Copy,
  Check,
  Code,
  Lightbulb,
} from "lucide-react";
import { type BlogPost } from "@/lib/data/blogs";

interface BlogSidebarProps {
  popularPosts: BlogPost[];
  categories: string[];
  selectedCategory: string | null;
  onCategoryChange: (category: string | null) => void;
}

// Featured Code Snippet Component
const FeaturedSnippet: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const snippet = `// Build something amazing
const developer = {
  name: "You",
  skills: ["code", "design", "ship"],
  impact: Infinity
};`;

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border/40 p-5 bg-gradient-to-br from-foreground/[0.02] to-primary/[0.02] space-y-4"
    >
      <div className="flex items-center gap-2">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Code className="w-4 h-4 text-primary" />
        </div>
        <h3 className="text-sm font-bold text-foreground">Dev Snippet</h3>
      </div>

      <div className="bg-background/80 border border-border/30 rounded-lg p-3 font-mono text-[10px] leading-relaxed text-muted-foreground overflow-x-auto">
        <pre>{snippet}</pre>
      </div>

      <button
        onClick={handleCopy}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-foreground/5 border border-border/30 rounded-lg hover:bg-foreground/10 transition-all text-[10px] font-bold text-muted-foreground hover:text-foreground"
      >
        {copied ? (
          <>
            <Check className="w-3 h-3" />
            Copied!
          </>
        ) : (
          <>
            <Copy className="w-3 h-3" />
            Copy Code
          </>
        )}
      </button>
    </motion.div>
  );
};

// Tech Stack Component
const TechStack: React.FC = () => {
  const techs = [
    { name: "React", color: "text-blue-500" },
    { name: "TypeScript", color: "text-blue-600" },
    { name: "Next.js", color: "text-foreground" },
    { name: "Tailwind", color: "text-cyan-500" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border/40 p-5 bg-gradient-to-br from-foreground/[0.02] to-primary/[0.02] space-y-4"
    >
      <h3 className="text-xs font-bold uppercase tracking-widest text-foreground">
        Tech Stack
      </h3>

      <div className="grid grid-cols-2 gap-2">
        {techs.map((tech) => (
          <motion.div
            key={tech.name}
            whileHover={{ scale: 1.05 }}
            className="px-3 py-2 rounded-lg bg-background border border-border/30 hover:border-primary/40 transition-all text-center"
          >
            <p className={`text-[10px] font-bold ${tech.color}`}>{tech.name}</p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

// Dev Tips Component
const DevTips: React.FC = () => {
  const [currentTip, setCurrentTip] = useState(0);

  const tips = [
    {
      title: "Keep it Simple",
      desc: "Write code that your future self can understand.",
    },
    {
      title: "Test Early",
      desc: "Catch bugs before they reach production.",
    },
    {
      title: "Document Always",
      desc: "Good docs save hours of debugging later.",
    },
    {
      title: "Refactor Often",
      desc: "Clean code is happy code.",
    },
  ];

  const nextTip = () => {
    setCurrentTip((prev) => (prev + 1) % tips.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border/40 p-5 bg-gradient-to-br from-foreground/[0.02] to-primary/[0.02] space-y-4"
    >
      <div className="flex items-center gap-2">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Lightbulb className="w-4 h-4 text-primary" />
        </div>
        <h3 className="text-sm font-bold text-foreground">Dev Tips</h3>
      </div>

      <motion.div
        key={currentTip}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-2"
      >
        <p className="text-xs font-bold text-primary">
          {tips[currentTip].title}
        </p>
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          {tips[currentTip].desc}
        </p>
      </motion.div>

      <button
        onClick={nextTip}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-foreground/5 border border-border/30 rounded-lg hover:bg-foreground/10 transition-all text-[10px] font-bold text-muted-foreground hover:text-foreground"
      >
        Next Tip
        <ArrowRight className="w-3 h-3" />
      </button>
    </motion.div>
  );
};

export const BlogSidebar: React.FC<BlogSidebarProps> = ({
  popularPosts,
  categories,
  selectedCategory,
  onCategoryChange,
}) => {
  return (
    <aside className="space-y-6 h-fit sticky top-24">
      {/* Dev Tips */}
      <DevTips />

      {/* Topics / Categories List */}
      <div className="space-y-6">
        <h3 className="text-xs font-bold uppercase tracking-widest text-foreground">
          Explore Topics
        </h3>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => onCategoryChange(cat === "All" ? null : cat)}
              className="px-3 py-1.5 rounded-lg text-[10px] font-bold border border-border/50 hover:border-primary hover:text-primary transition-all text-muted-foreground uppercase tracking-wider"
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
};

export default BlogSidebar;
